/*
  Copyright 2018-2020 National Geographic Society

  Use of this software does not constitute endorsement by National Geographic
  Society (NGS). The NGS name and NGS logo may not be used for any purpose without
  written permission from NGS.

  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { merge } from 'lodash/fp';
import { Deserializer } from 'ts-jsonapi';

import { encodeQueryToURL } from '@marapp/earth-shared';

import { MAP_API_URL } from '../../config';

export interface RequestQuery {
  [key: string]: any;
}
export type RequestMethod = 'get' | 'post' | 'put' | 'delete';
export interface RequestConfig {
  query?: RequestQuery;
  method?: RequestMethod;
  data?: any;
}

const TIMEOUT = 30000;

export const DeserializerService = new Deserializer({
  keyForAttribute: (attribute: any) => {
    return attribute;
  },
});

export const BaseAPIService = {
  request: (
    path: string,
    config: RequestConfig,
    deserializer: (response: AxiosResponse) => any = BaseAPIService.deserialize,
    options?: AxiosRequestConfig
  ) => {
    const defaults = { query: {}, method: 'get', data: {} };
    const params = merge(defaults, config);

    const requestOptions: AxiosRequestConfig = {
      baseURL: MAP_API_URL,
      url: encodeQueryToURL(path, params.query),
      method: params.method,
      data: params.data,
      timeout: TIMEOUT,
      ...options,
    };

    // Log the outgoing request details
    // console.log('[BaseAPIService.request] Outgoing request:', {
    //   fullUrl: `${requestOptions.baseURL}${requestOptions.url}`,
    //   method: requestOptions.method,
    //   data: requestOptions.data,
    //   query: params.query,
    // });

    return new Promise((resolve, reject) => {
      axios
        .request(requestOptions)
        .then((response) => resolve(deserializer(response)))
        .catch((error) => {
          // Log the failing request details
          console.error('[BaseAPIService.request] Request FAILED:', {
            fullUrl: `${requestOptions.baseURL}${requestOptions.url}`,
            method: requestOptions.method,
            data: requestOptions.data,
            status: error?.response?.status,
            statusText: error?.response?.statusText,
            responseData: error?.response?.data,
          });

          reject(error?.response?.data?.data ? deserializer(error?.response) : error?.response);
        });
    });
  },

  requestSWR: (
    path: string,
    config: RequestConfig,
    deserializer: (response: AxiosResponse) => any = BaseAPIService.deserialize
  ) => {
    const defaults = { query: {}, method: 'get', data: {} };
    const params = merge(defaults, config);

    const options: AxiosRequestConfig = {
      baseURL: MAP_API_URL,
      url: path,
      method: params.method,
      data: params.data,
      timeout: TIMEOUT,
    };

    // Log the outgoing request details
    // console.log('[BaseAPIService.requestSWR] Outgoing request:', {
    //   fullUrl: `${options.baseURL}${options.url}`,
    //   method: options.method,
    //   data: options.data,
    // });

    return new Promise((resolve, reject) => {
      axios
        .request(options) // Note: Fixed bug - was using undefined `requestOptions`
        .then((response) => resolve(deserializer(response)))
        .catch((error) => {
          // Log the failing request details
          console.error('[BaseAPIService.requestSWR] Request FAILED:', {
            fullUrl: `${options.baseURL}${options.url}`,
            method: options.method,
            data: options.data,
            status: error?.response?.status,
            statusText: error?.response?.statusText,
            responseData: error?.response?.data,
          });

          reject(error?.response?.data?.data ? deserializer(error?.response) : error?.response);
        });
    });
  },
  /**
   * JSON:API deserializer
   * @param response
   */
  deserialize: (response: AxiosResponse): any => {
    try {
      if (!response || !response.data) {
        // Log a warning if the response or response data is missing
        console.warn('Warning: Response or response data is missing. Unable to deserialize.');
        return null; // Return null to indicate failure
      }

      // Attempt to deserialize the response data
      return DeserializerService.deserialize(response.data);
    } catch (error) {
      // Log an error if deserialization fails
      console.error('Error during deserialization:', error);
      return null; // Return null to indicate failure
    }
  },
};

/**
 * JSON:API meta deserializer
 * @param response
 */
export const metaDeserializer = (response: AxiosResponse): any => {
  try {
    if (!response || !response.data) {
      // Log a warning if the response or response data is missing
      console.warn('Warning: Response or response data is missing. Unable to deserialize meta.');
      return { data: null, meta: null }; // Return null values to indicate failure
    }

    // Attempt to deserialize the response data and meta
    return {
      data: DeserializerService.deserialize(response.data),
      meta: response.data.meta || {}, // Default to an empty object if meta is missing
    };
  } catch (error) {
    // Log an error if deserialization fails
    console.error('Error during meta deserialization:', error);
    return { data: null, meta: null }; // Return null values to indicate failure
  }
};

/**
 * Generate a cache key based on URL path and query params.
 * @param path
 * @param query
 */
export const generateCacheKey = (path: string, query: RequestQuery): string => {
  return encodeQueryToURL(path, query);
};
